import * as React from "react";
import { api } from "@/lib/api";

/**
 * useBrandingAccent — Phase 15.5c.
 *
 * Loads the per-company accent selection and applies it to the document
 * root as CSS variables. Any component that references `--accent`,
 * `--accent-pressed`, `--accent-subtle`, or Tailwind's `--ring` picks up
 * the change with zero prop drilling.
 *
 * The base tokens.css always paints the Fable violet fallback until
 * Phase 15.5e ships. This hook overlays whichever accent the org owner
 * has selected — including, deliberately, the platform default (sky) so
 * that the UI stops looking violet the moment 15.5c lands.
 *
 * Contract:
 *   • Fetch runs once per companyId. Skips silently when the caller
 *     doesn't hold `company.manage` (public / staff paths).
 *   • Provides an in-flight override (`preview(entry)`) so the Settings
 *     picker can live-swap the accent while the user browses swatches.
 *   • On unmount, restores to the persisted server value — never rips a
 *     paint out from under other pages.
 */

const HEX_VARS = [
  ["--accent", "light", "dark"],
  ["--accent-pressed", "pressed_light", "pressed_dark"],
  ["--accent-subtle", "subtle_light", "subtle_dark"],
];

function applyEntry(entry) {
  if (!entry) return;
  const root = document.documentElement;
  const dark = root.classList.contains("dark") ||
               root.getAttribute("data-theme") === "dark";
  for (const [cssVar, lightK, darkK] of HEX_VARS) {
    const val = dark ? entry[darkK] : entry[lightK];
    if (val) root.style.setProperty(cssVar, val);
  }
  // Bridge shadcn --ring + --primary so shadcn primitives (button, input,
  // select) pick the new accent as well.
  root.style.setProperty("--accent-hex", entry.light);
}

function clearOverride() {
  const root = document.documentElement;
  for (const [cssVar] of HEX_VARS) root.style.removeProperty(cssVar);
  root.style.removeProperty("--accent-hex");
}

export function useBrandingAccent(companyId, { enabled = true } = {}) {
  const [branding, setBranding] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!enabled || !companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/branding`);
      setBranding(data);
      applyEntry(data.accent);
    } catch {
      // 403 for staff, 404 for stale routes — either way, leave the base
      // tokens in place.
      setBranding(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  React.useEffect(() => { load(); }, [load]);

  // Reapply on light/dark theme swap (`data-theme` attr on <html>).
  React.useEffect(() => {
    const obs = new MutationObserver(() => {
      if (branding?.accent) applyEntry(branding.accent);
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => obs.disconnect();
  }, [branding]);

  const preview = React.useCallback((entry) => applyEntry(entry), []);
  const reset   = React.useCallback(() => {
    if (branding?.accent) applyEntry(branding.accent);
    else clearOverride();
  }, [branding]);

  return { branding, loading, reload: load, preview, reset };
}
